import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  buildRegistrationRequest,
  resolveRegisterErrorCode,
  type SubmitValues,
} from '../register.utils';
import { registrationKeys } from './registrationKeys';
import {
  UserRegistrationError,
  type UserRegistrationIntentResponse,
  UserRegistrationStatus,
} from '@/services/register.types';
import { submitRegistration } from '@/services/register.service';

export type { SubmitValues };

export interface IntentParams {
  esid?: string;
  it?: string;
}

interface UseRegisterMutationArgs {
  registrationToken: string;
  intentParams: IntentParams;
  onError: (errorCode: UserRegistrationError) => void;
}

const useRegisterMutation = ({
  registrationToken,
  intentParams,
  onError,
}: UseRegisterMutationArgs) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: SubmitValues) =>
      submitRegistration(buildRegistrationRequest(values, registrationToken)),
    onSuccess: ({
      data: {
        anonymizedConfirmationCodeRecipient,
        confirmationMethod,
        redirectUrl,
      },
    }) => {
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      queryClient.setQueryData<UserRegistrationIntentResponse>(
        registrationKeys.intent(intentParams.esid, intentParams.it),
        (prev) =>
          prev
            ? {
                ...prev,
                anonymizedConfirmationCodeRecipient,
                confirmationMethod,
                registrationStatus:
                  UserRegistrationStatus.PENDING_USER_ACCOUNT_CONFIRMATION,
              }
            : prev,
      );
    },
    onError: (error) => onError(resolveRegisterErrorCode(error)),
  });
};

export default useRegisterMutation;
